// Shared GPS fetch helper. Returns a promise that resolves with
// { latitude, longitude, accuracy } or rejects with a human-readable message.
//
// Fixes silent-failure pitfalls:
//  - Missing navigator.geolocation (insecure context / iframe)
//  - Permission denied
//  - Timeout hanging forever
//  - Permissions-Policy blocking (iframes)
export function fetchGps({ timeoutMs = 12000, highAccuracy = true } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) {
      return reject(new Error(
        "Geolocation is not available. Ensure the site is loaded over HTTPS and the browser supports GPS.",
      ));
    }
    if (!window.isSecureContext) {
      return reject(new Error(
        "GPS requires a secure (HTTPS) connection. Please reload over HTTPS.",
      ));
    }

    let settled = false;
    const finish = (fn, arg) => { if (!settled) { settled = true; fn(arg); } };

    const timer = setTimeout(() => {
      finish(reject, new Error(
        "Took too long to locate you. Allow location access and try again (or move near a window).",
      ));
    }, timeoutMs);

    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        finish(resolve, {
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracy: p.coords.accuracy,
        });
      },
      (err) => {
        clearTimeout(timer);
        const msg =
          err.code === 1 ? "Location permission denied — enable it in your browser address-bar."
          : err.code === 2 ? "Your device couldn't determine the location. Check GPS / Wi-Fi."
          : err.code === 3 ? "Locating timed out. Try again or enter coordinates manually."
          : "Could not fetch GPS. " + (err.message || "");
        finish(reject, new Error(msg));
      },
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/**
 * Reverse-geocode lat/lng to a human-readable address via OpenStreetMap
 * Nominatim (free, no key). Returns { display_name, house_number, road,
 * neighbourhood, suburb, village, town, city, county, state, postcode, country }
 * or throws on network / rate-limit failure.
 *
 * Nominatim's usage policy limits to 1 request/sec — this helper is only
 * ever invoked in response to a user action (form load or explicit button),
 * never in a hot loop.
 */
export async function reverseGeocode(latitude, longitude) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;
  const res = await fetch(url, {
    headers: { "Accept": "application/json", "Accept-Language": "en" },
  });
  if (!res.ok) throw new Error(`Reverse-geocode failed (${res.status})`);
  const data = await res.json();
  const a = data.address || {};
  return {
    display_name: data.display_name || "",
    house_number: a.house_number || "",
    road: a.road || a.pedestrian || "",
    neighbourhood: a.neighbourhood || a.suburb || a.village || a.hamlet || "",
    city: a.city || a.town || a.village || a.county || "",
    state: a.state || "",
    postcode: a.postcode || "",
    country: a.country || "",
  };
}
